import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useOnboarding } from "@/hooks/useOnboarding";
import { useAuthModal } from "@/hooks/useAuthModal";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import "@/styles/driver-theme.css";
import { Header } from "@/components/Header";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Copy,
  Download,
  ExternalLink,
  AlertTriangle,
  Flag,
  ChevronDown,
  FileSpreadsheet,
  X,
  Check,
  Bookmark,
  Share2,
  ThumbsUp,
  ThumbsDown,
  MoreHorizontal,
  User,
  CheckCircle2,
  MousePointerClick,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import verifiedBadge from "@/assets/verified-badge.png";
import mouseIcon from "@/assets/mouse-icon.png";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { DiscussionSection } from "@/components/DiscussionSection";
import { RecommendedMacroCard } from "@/components/RecommendedMacroCard";
import { getTagColor } from "@/lib/tagColors";
import { formatReadmeContent } from "@/lib/formatReadmeContent";
import { cn, formatFollowerCount } from "@/lib/utils";
import { VBACodeBlock } from "@/components/VBACodeBlock";
interface VbaModule {
  id: string;
  name: string;
  code: string;
  riskScan?: {
    score: string;
    flags: any[];
  };
}
interface ExcelFile {
  name: string;
  size: number;
  type: string;
  url?: string;
}
const MacroDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { markOnboardingComplete } = useOnboarding();
  const { openAuthModal } = useAuthModal();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const [macro, setMacro] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<VbaModule[]>([]);
  const [excelFiles, setExcelFiles] = useState<ExcelFile[]>([]);
  const [openModules, setOpenModules] = useState<Set<string>>(new Set());
  const [discussionSortBy, setDiscussionSortBy] = useState<"newest" | "top" | "helpful">("top");
  const [averageRating, setAverageRating] = useState<number | null>(null);
  const [authorFollowerCount, setAuthorFollowerCount] = useState(0);
  const [copiedModuleId, setCopiedModuleId] = useState<string | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState("");
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [recommendedMacros, setRecommendedMacros] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLiked, setIsLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [isDisliked, setIsDisliked] = useState(false);
  const [likeAnimation, setLikeAnimation] = useState(false);
  const [dislikeAnimation, setDislikeAnimation] = useState(false);
  const [showAddToExcelPopover, setShowAddToExcelPopover] = useState(false);
  const [isCopiedMobile, setIsCopiedMobile] = useState(false);
  const [selectedOS, setSelectedOS] = useState<"windows" | "mac">(() => {
    const saved = localStorage.getItem("macrovault-os-preference");
    return (saved === "mac" ? "mac" : "windows") as "windows" | "mac";
  });

  // Persist OS selection to localStorage
  useEffect(() => {
    localStorage.setItem("macrovault-os-preference", selectedOS);
  }, [selectedOS]);
  
  // Check saved/liked status when user or macro changes
  useEffect(() => {
    if (user && macro?.id) {
      checkIfSaved(user.id);
      checkIfLiked(user.id, macro.id);
    }
  }, [user, macro?.id]);
  useEffect(() => {
    if (slug) {
      window.scrollTo(0, 0);
      fetchMacro();
      // Increment view count
      incrementViewCount();
    }
  }, [slug]);

  // Fetch recommended macros
  useEffect(() => {
    const fetchRecommended = async () => {
      if (!macro?.id) return;
      const { data, error } = await supabase
        .from("macros")
        .select(
          `
          id,
          slug,
          name,
          installs_count,
          screenshot_url,
          public_profiles (username, full_name),
          macro_tags (tags (name))
        `,
        )
        .eq("is_published", true)
        .eq("is_approved", true)
        .neq("id", macro.id)
        .order("installs_count", {
          ascending: false,
        })
        .limit(15);
      if (!error && data) {
        // Extract category from nested macro_tags structure
        const macrosWithCategory = data.map((macro: any) => ({
          ...macro,
          category: macro.macro_tags?.[0]?.tags?.name || null,
        }));
        setRecommendedMacros(macrosWithCategory);
      }
    };
    fetchRecommended();
  }, [macro?.id]);

  // Continue onboarding tour if user came from vault
  useEffect(() => {
    const continueStep = sessionStorage.getItem("continueOnboardingStep");

    if (continueStep === "2" && macro && modules.length > 0) {
      // Wait for page to fully render with retry logic
      let attempts = 0;
      const maxAttempts = 10;

      const checkAndStart = () => {
        const codeBlock = document.querySelector("[data-vba-code-block]");
        const addToExcelBtn = document.querySelector("[data-add-to-excel]");

        if (codeBlock && addToExcelBtn) {
          sessionStorage.removeItem("continueOnboardingStep");
          continueOnboardingTour();
        } else if (attempts < maxAttempts) {
          attempts++;
          setTimeout(checkAndStart, 200);
        }
      };

      // Start checking after initial delay
      const timer = setTimeout(checkAndStart, 500);

      return () => clearTimeout(timer);
    }
  }, [macro, modules]);

  const continueOnboardingTour = () => {
    const driverObj = driver({
      showProgress: false,
      showButtons: ["next"],
      animate: true,
      smoothScroll: true,
      allowClose: false,
      disableActiveInteraction: true,
      steps: [
        {
          element: "[data-vba-code-block]",
          popover: {
            title: "💻 Ready-to-use VBA code",
            description: "Browse the code and description to understand what this macro does.",
            side: "left",
            align: "start",
            onPopoverRender: (popover) => {
              // Add smooth fade-in animation
              popover.wrapper.style.opacity = "0";
              popover.wrapper.style.transition = "opacity 0.3s ease-out";
              requestAnimationFrame(() => {
                popover.wrapper.style.opacity = "1";
              });

              // Add custom X button
              const closeBtn = document.createElement("button");
              closeBtn.innerHTML = "×";
              closeBtn.className = `
                absolute top-2 right-2
                w-8 h-8
                flex items-center justify-center
                text-2xl text-gray-400 hover:text-gray-600
                rounded-md hover:bg-gray-100
                transition-colors
                z-10
              `;
              closeBtn.onclick = () => {
                driverObj.destroy();
                markOnboardingComplete();
              };
              popover.wrapper.appendChild(closeBtn);

              const footer = popover.footer;
              footer.innerHTML = "";

              const progressText = document.createElement("div");
              progressText.className = "text-xs text-gray-400 font-normal mb-1";
              progressText.textContent = "2 of 4";

              const nextButton = document.createElement("button");
              nextButton.textContent = "Next";
              nextButton.className = `
                px-8 py-4
                rounded-xl
                font-semibold text-lg
                bg-primary text-primary-foreground
                hover:bg-primary/90
                shadow-sm
                transition-all
              `;
              nextButton.onclick = () => driverObj.moveNext();

              footer.appendChild(progressText);
              footer.appendChild(nextButton);
            },
          },
        },
        {
          element: "[data-add-to-excel]",
          popover: {
            title: "⚡ Copy code instantly",
            description: 'Click "Add to Excel" to copy the code and get step-by-step instructions.',
            side: "top",
            align: "end",
            onPopoverRender: (popover) => {
              // Add smooth fade-in animation
              popover.wrapper.style.opacity = "0";
              popover.wrapper.style.transition = "opacity 0.3s ease-out";
              requestAnimationFrame(() => {
                popover.wrapper.style.opacity = "1";
              });

              // Add custom X button
              const closeBtn = document.createElement("button");
              closeBtn.innerHTML = "×";
              closeBtn.className = `
                absolute top-2 right-2
                w-8 h-8
                flex items-center justify-center
                text-2xl text-gray-400 hover:text-gray-600
                rounded-md hover:bg-gray-100
                transition-colors
                z-10
              `;
              closeBtn.onclick = () => {
                driverObj.destroy();
                markOnboardingComplete();
              };
              popover.wrapper.appendChild(closeBtn);

              const footer = popover.footer;
              footer.innerHTML = "";

              const progressText = document.createElement("div");
              progressText.className = "text-xs text-gray-400 font-normal mb-1";
              progressText.textContent = "3 of 4";

              const nextButton = document.createElement("button");
              nextButton.textContent = "Next";
              nextButton.className = `
                px-8 py-4
                rounded-xl
                font-semibold text-lg
                bg-primary text-primary-foreground
                hover:bg-primary/90
                shadow-sm
                transition-all
              `;
              nextButton.onclick = () => driverObj.moveNext();

              footer.appendChild(progressText);
              footer.appendChild(nextButton);
            },
          },
        },
        {
          element: window.innerWidth < 768 ? "[data-onboarding-profile]" : "[data-onboarding-upload]",
          popover: {
            title: "📤 Upload your own",
            description: window.innerWidth < 768 
              ? "Tap your profile picture to open the menu, then select Upload to share your VBA macros with the community. That's it—you're ready to go!"
              : "Share your VBA macros with the community. That's it—you're ready to go!",
            side: "bottom",
            align: window.innerWidth < 768 ? "end" : "start",
            onPopoverRender: (popover) => {
              sessionStorage.setItem("continueOnboardingStep", "4");

              // Add smooth fade-in animation
              popover.wrapper.style.opacity = "0";
              popover.wrapper.style.transition = "opacity 0.3s ease-out";
              requestAnimationFrame(() => {
                popover.wrapper.style.opacity = "1";
              });

              // Add custom X button
              const closeBtn = document.createElement("button");
              closeBtn.innerHTML = "×";
              closeBtn.className = `
                absolute top-2 right-2
                w-8 h-8
                flex items-center justify-center
                text-2xl text-gray-400 hover:text-gray-600
                rounded-md hover:bg-gray-100
                transition-colors
                z-10
              `;
              closeBtn.onclick = () => {
                driverObj.destroy();
                markOnboardingComplete();
              };
              popover.wrapper.appendChild(closeBtn);

              const footer = popover.footer;
              footer.innerHTML = "";

              const progressText = document.createElement("div");
              progressText.className = "text-xs text-gray-400 font-normal mb-1";
              progressText.textContent = "4 of 4";

              const nextButton = document.createElement("button");
              nextButton.textContent = "Finish";
              nextButton.className = `
                px-8 py-4
                rounded-xl
                font-semibold text-lg
                bg-primary text-primary-foreground
                hover:bg-primary/90
                shadow-sm
                transition-all
              `;
              nextButton.onclick = () => {
                markOnboardingComplete();
                driverObj.destroy();
              };

              footer.appendChild(progressText);
              footer.appendChild(nextButton);
            },
          },
        },
      ],
      onDestroyed: () => {
        markOnboardingComplete();
      },
    });

    driverObj.drive();
  };

  const checkIfSaved = async (userId: string) => {
    if (!macro?.id) return;
    const { data } = await supabase
      .from("saved_macros")
      .select("id")
      .eq("user_id", userId)
      .eq("macro_id", macro.id)
      .maybeSingle();
    setIsSaved(!!data);
  };
  const checkIfLiked = async (userId: string, macroId: string) => {
    const { data } = await supabase
      .from("macro_likes")
      .select("id")
      .eq("macro_id", macroId)
      .eq("user_id", userId)
      .maybeSingle();
    setIsLiked(!!data);
  };
  const handleSaveToggle = async () => {
    if (!user) {
      openAuthModal(async () => {
        // Retry the save action after successful login
        if (!macro?.id) return;
        try {
          const {
            data: { user: newUser },
          } = await supabase.auth.getUser();
          if (!newUser) return;

          await supabase.from("saved_macros").insert({
            user_id: newUser.id,
            macro_id: macro.id,
          });
          setIsSaved(true);
          toast.success("Saved!");
        } catch (error) {
          console.error("Error saving:", error);
          toast.error("Failed to save");
        }
      });
      return;
    }
    if (!macro?.id) return;
    try {
      if (isSaved) {
        await supabase.from("saved_macros").delete().eq("user_id", user.id).eq("macro_id", macro.id);
        setIsSaved(false);
        toast.success("Removed from saved");
      } else {
        await supabase.from("saved_macros").insert({
          user_id: user.id,
          macro_id: macro.id,
        });
        setIsSaved(true);
        toast.success("Saved!");
      }
    } catch (error) {
      console.error("Error toggling save:", error);
      toast.error("Failed to save");
    }
  };
  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    toast.success("Link copied to clipboard!");
  };

  const handleAddToExcel = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    const codeToCopy = modules.map((m) => `' Module: ${m.name}\n${m.code}`).join("\n\n");
    navigator.clipboard.writeText(codeToCopy);
    setShowAddToExcelPopover(true);
    await recordInstall("copy");
  };
  const handleLike = async () => {
    if (!user) {
      openAuthModal(async () => {
        // Retry the like action after successful login
        if (!macro) return;
        try {
          const {
            data: { user: newUser },
          } = await supabase.auth.getUser();
          if (!newUser) return;

          const { error } = await supabase.from("macro_likes").insert({
            macro_id: macro.id,
            user_id: newUser.id,
          });
          if (error) throw error;
          setIsLiked(true);
          setLikesCount((prev) => prev + 1);
          toast.success("Liked!");
        } catch (error) {
          console.error("Error liking:", error);
          toast.error("Failed to like");
        }
      });
      return;
    }
    if (!macro) return;

    // Trigger animation
    setLikeAnimation(true);
    setTimeout(() => setLikeAnimation(false), 300);
    try {
      if (isLiked) {
        // Unlike
        const { error } = await supabase.from("macro_likes").delete().eq("macro_id", macro.id).eq("user_id", user.id);
        if (error) throw error;
        setIsLiked(false);
        setLikesCount((prev) => Math.max(0, prev - 1));
        toast.success("Removed like");
      } else {
        // Like
        const { error } = await supabase.from("macro_likes").insert({
          macro_id: macro.id,
          user_id: user.id,
        });
        if (error) throw error;
        setIsLiked(true);
        setIsDisliked(false); // Remove dislike if they like
        setLikesCount((prev) => prev + 1);
        toast.success("Liked!");
      }
    } catch (error) {
      console.error("Error toggling like:", error);
      toast.error("Failed to update like");
    }
  };
  const handleDislike = async () => {
    if (!user) {
      openAuthModal(async () => {
        // Retry the dislike action after successful login
        if (!macro) return;
        try {
          const {
            data: { user: newUser },
          } = await supabase.auth.getUser();
          if (!newUser) return;

          setIsDisliked(true);
          // Check if user had previously liked and remove it
          const { data: existingLike } = await supabase
            .from("macro_likes")
            .select("id")
            .eq("macro_id", macro.id)
            .eq("user_id", newUser.id)
            .maybeSingle();

          if (existingLike) {
            await supabase.from("macro_likes").delete().eq("macro_id", macro.id).eq("user_id", newUser.id);
            setIsLiked(false);
            setLikesCount((prev) => Math.max(0, prev - 1));
          }
        } catch (error) {
          console.error("Error disliking:", error);
          toast.error("Failed to dislike");
        }
      });
      return;
    }
    if (!macro) return;

    // Trigger animation
    setDislikeAnimation(true);
    setTimeout(() => setDislikeAnimation(false), 300);
    try {
      if (isDisliked) {
        // Remove dislike (just toggle off)
        setIsDisliked(false);
      } else {
        // Dislike
        setIsDisliked(true);

        // If user had previously liked, remove their like AND subtract from count
        if (isLiked) {
          const { error } = await supabase.from("macro_likes").delete().eq("macro_id", macro.id).eq("user_id", user.id);
          if (error) throw error;
          setIsLiked(false);
          setLikesCount((prev) => Math.max(0, prev - 1)); // Subtract from count
        }
      }
    } catch (error) {
      console.error("Error toggling dislike:", error);
      toast.error("Failed to update dislike");
    }
  };
  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast.error("Please provide a reason for reporting");
      return;
    }
    const { error } = await supabase.from("reports").insert({
      macro_id: macro?.id,
      reason: reportReason,
      user_id: user?.id || null,
    });
    if (error) {
      toast.error("Failed to submit report");
      console.error(error);
    } else {
      toast.success("Report submitted successfully");
      setShowReportDialog(false);
      setReportReason("");
    }
  };
  const incrementViewCount = async () => {
    try {
      await (supabase as any).rpc("increment_macro_views", {
        macro_slug: slug,
      });
    } catch (error) {
      console.error("Error incrementing view count:", error);
    }
  };

  // Parse modules and excel files when macro changes
  useEffect(() => {
    if (macro) {
      parseVersionData(macro);
    }
  }, [macro]);
  const parseVersionData = (macroData: any) => {
    // Parse modules from macro data
    if (macroData?.code_text) {
      try {
        const parsedModules = JSON.parse(macroData.code_text);
        if (Array.isArray(parsedModules)) {
          setModules(parsedModules);
          // Open first module by default
          if (parsedModules.length > 0) {
            setOpenModules(new Set([parsedModules[0].id]));
          }
        } else {
          // Legacy: single code block, removing any separator lines
          let cleanCode = macroData.code_text
            .split("\n")
            .filter((line: string) => !line.match(/^'={10,}\s*Module:\s*.+?\s*={10,}/i))
            .join("\n")
            .trim();
          setModules([
            {
              id: "main",
              name: "Main Module",
              code: cleanCode,
              riskScan: macroData.risk_flags
                ? {
                    score: macroData.risk_score,
                    flags: macroData.risk_flags,
                  }
                : undefined,
            },
          ]);
          setOpenModules(new Set(["main"]));
        }
      } catch {
        // Check if it's a combined string with module separators
        const modulePattern = /={10,}\s*Module:\s*(.+?)\s*={10,}/gi;
        const matches = [...macroData.code_text.matchAll(modulePattern)];
        if (matches.length > 1) {
          // Multiple modules detected - split them
          const extractedModules: VbaModule[] = [];
          for (let i = 0; i < matches.length; i++) {
            const moduleName = matches[i][1].trim();
            const startIdx = matches[i].index! + matches[i][0].length;
            const endIdx = i < matches.length - 1 ? matches[i + 1].index! : macroData.code_text.length;
            let moduleCode = macroData.code_text.substring(startIdx, endIdx).trim();

            // Remove any remaining separator lines from the code
            moduleCode = moduleCode
              .split("\n")
              .filter((line: string) => !line.match(/^'={10,}\s*Module:\s*.+?\s*={10,}/i))
              .join("\n")
              .trim();
            extractedModules.push({
              id: `module-${i}`,
              name: moduleName,
              code: moduleCode,
              riskScan: macroData.risk_flags
                ? {
                    score: macroData.risk_score,
                    flags: macroData.risk_flags,
                  }
                : undefined,
            });
          }
          setModules(extractedModules);
          setOpenModules(new Set([extractedModules[0].id]));
        } else {
          // Fallback: treat as single code block, removing any separator lines
          let cleanCode = macroData.code_text
            .split("\n")
            .filter((line: string) => !line.match(/^'={10,}\s*Module:\s*.+?\s*={10,}/i))
            .join("\n")
            .trim();
          setModules([
            {
              id: "main",
              name: "Main Module",
              code: cleanCode,
              riskScan: macroData.risk_flags
                ? {
                    score: macroData.risk_score,
                    flags: macroData.risk_flags,
                  }
                : undefined,
            },
          ]);
          setOpenModules(new Set(["main"]));
        }
      }
    }

    // Parse Excel files from metadata
    if (macroData?.sample_file_url) {
      try {
        const parsedFiles = JSON.parse(macroData.sample_file_url);
        if (Array.isArray(parsedFiles)) {
          setExcelFiles(parsedFiles);
        } else {
          setExcelFiles([]);
        }
      } catch {
        setExcelFiles([]);
      }
    } else {
      setExcelFiles([]);
    }
  };
  const fetchMacro = async () => {
    // Reset all state before fetching new macro
    setLoading(true);
    setMacro(null);
    setModules([]);
    setExcelFiles([]);
    setIsSaved(false);
    setIsLiked(false);
    setLikesCount(0);
    setAverageRating(null);
    setAuthorFollowerCount(0);
    setRecommendedMacros([]);
    setOpenModules(new Set());
    
    const { data: macroData } = await (supabase as any)
      .from("macros")
      .select(
        `
        *,
        profiles!macros_owner_id_fkey (username, full_name, verified, avatar_url),
        macro_tags (tags (name))
      `,
      )
      .eq("slug", slug)
      .single();
    
    // Rename profiles to public_profiles for consistency
    if (macroData && macroData.profiles) {
      macroData.public_profiles = macroData.profiles;
      delete macroData.profiles;
    }
    if (macroData) {
      setMacro(macroData);
      setLikesCount(macroData.likes_count || 0);

      // Check if user has liked this macro
      if (user) {
        const { data: likeData } = await supabase
          .from("macro_likes")
          .select("id")
          .eq("macro_id", macroData.id)
          .eq("user_id", user.id)
          .maybeSingle();
        setIsLiked(!!likeData);
      }

      // Fetch author's follower count
      const { count: followers } = await supabase
        .from('follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', macroData.owner_id);
      setAuthorFollowerCount(followers || 0);

      // Fetch average rating
      await fetchAverageRating(macroData.id);
    }
    setLoading(false);
  };
  const fetchAverageRating = async (macroId: string) => {
    const { data: reviewsData } = await (supabase as any).from("reviews").select("rating").eq("macro_id", macroId);
    if (reviewsData && reviewsData.length > 0) {
      const avg = reviewsData.reduce((sum: number, r: any) => sum + r.rating, 0) / reviewsData.length;
      setAverageRating(avg);
    } else {
      setAverageRating(null);
    }
  };
  const handleCopyCode = (moduleCode?: string, moduleId?: string) => {
    const codeToCopy = moduleCode || modules.map((m) => `' Module: ${m.name}\n${m.code}`).join("\n\n");
    navigator.clipboard.writeText(codeToCopy);
    toast.success("Code copied to clipboard!");
    if (moduleId) {
      setCopiedModuleId(moduleId);
      setTimeout(() => setCopiedModuleId(null), 2000);
    }
    // Always record install when copying code
    recordInstall("copy");
  };
  const handleDownloadBas = () => {
    // Combine all modules into a single .bas file content
    const basContent = modules.map((m) => `' Module: ${m.name}\n${m.code}`).join("\n\n");

    // Create a blob and download
    const blob = new Blob([basContent], {
      type: "text/plain",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${macro.slug}.bas`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Download started!");
    recordInstall("download");
  };
  const toggleModule = (moduleId: string) => {
    setOpenModules((prev) => {
      const next = new Set(prev);
      if (next.has(moduleId)) {
        next.delete(moduleId);
      } else {
        next.add(moduleId);
      }
      return next;
    });
  };
  const getRiskColor = (score: string) => {
    switch (score) {
      case "high":
        return "text-destructive";
      case "medium":
        return "text-warning";
      case "low":
        return "text-success";
      default:
        return "text-muted-foreground";
    }
  };
  const getRiskBadgeVariant = (score: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (score) {
      case "high":
        return "destructive";
      case "medium":
        return "outline";
      case "low":
        return "secondary";
      default:
        return "secondary";
    }
  };
  const handleOpenInMacroFlow = () => {
    const baseUrl = import.meta.env.VITE_MACROFLOW_IMPORT_BASE_URL || "https://macroflow.ai/import";
    const url = `${baseUrl}?macroId=${macro.id}&version=${macro.semver || '1.0.0'}`;
    window.open(url, "_blank");
    recordInstall("macroflow_deeplink");
  };
  const recordInstall = async (source: string) => {
    try {
      if (!user) {
        // For anonymous users, use the anonymous install function
        const { error } = await supabase.rpc("record_anonymous_install", {
          p_macro_id: macro.id,
          p_source: source,
        });
        if (error) {
          console.error("Error recording anonymous install:", error);
          return;
        }
      } else {
        // For logged-in users, use the unique install function
        const { data, error } = await supabase.rpc("record_unique_install", {
          p_macro_id: macro.id,
          p_user_id: user.id,
          p_source: source,
        });
        if (error) {
          console.error("Error recording install:", error);
          return;
        }
      }

      // Update install count locally without refetching
      setMacro((prev: any) => ({
        ...prev,
        installs_count: (prev?.installs_count || 0) + 1,
      }));
    } catch (error) {
      console.error("Error recording install:", error);
    }
  };
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          showSearch={true}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={() => {
            if (searchQuery.trim()) {
              navigate(`/vault?search=${encodeURIComponent(searchQuery)}`);
            }
          }}
        />
        <div className="container mx-auto px-4 py-8">
          <p>Loading...</p>
        </div>
      </div>
    );
  }
  if (!macro) {
    return (
      <div className="min-h-screen bg-background">
        <Header
          showSearch={true}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSearchSubmit={() => {
            if (searchQuery.trim()) {
              navigate(`/vault?search=${encodeURIComponent(searchQuery)}`);
            }
          }}
        />
        <div className="container mx-auto px-4 py-8">
          <p>Macro not found</p>
        </div>
      </div>
    );
  }
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Helmet>
        <title>Excel VBA Macro to {macro.name} | Free Download | MacroVault</title>
        <meta name="description" content={`${macro.short_desc || macro.name} - Free Excel VBA macro. Copy, paste, and run in seconds. No installation required.`} />
        <meta name="keywords" content={`Excel macro, VBA, ${macro.name}, Excel automation, free macro, ${macro.tags?.map((t: any) => t.name).join(", ")}`} />
        <link rel="canonical" href={`https://findmacros.com/m/${macro.slug}`} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="MacroVault" />
        <meta property="og:title" content={`Excel VBA Macro: ${macro.name}`} />
        <meta property="og:description" content={macro.short_desc || `Free ${macro.name} VBA macro for Excel`} />
        <meta property="og:url" content={`https://findmacros.com/m/${macro.slug}`} />
        <meta property="og:image" content={macro.screenshot_url || 'https://findmacros.com/og-image.png'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Excel VBA Macro: ${macro.name}`} />
        <meta name="twitter:description" content={macro.short_desc || `Free ${macro.name} VBA macro for Excel`} />
        <meta name="twitter:image" content={macro.screenshot_url || 'https://findmacros.com/og-image.png'} />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": macro.name,
            "description": macro.short_desc || macro.name,
            "applicationCategory": "Productivity",
            "operatingSystem": ["Windows", "macOS"],
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "aggregateRating": averageRating ? {
              "@type": "AggregateRating",
              "ratingValue": averageRating,
              "ratingCount": macro.reviews_count || 0
            } : undefined,
            "author": {
              "@type": "Person",
              "name": macro.owner?.username || "MacroVault"
            }
          })}
        </script>
      </Helmet>
      <Header
        showSearch={true}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSearchSubmit={() => {
          if (searchQuery.trim()) {
            navigate(`/vault?search=${encodeURIComponent(searchQuery)}`);
          }
        }}
      />

      <div className="px-6 md:px-8 lg:px-12 py-6 sm:py-8 lg:py-12 pt-24 sm:pt-28 lg:pt-32">
        <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6 max-w-[1380px] mx-auto">
          {/* Left Column: Main Content */}
          <div className="min-w-0">
{/* All Modules as Tabs with Preview/Workbook options */}
            {modules.length > 0 && (
              <div className="mb-3">
                <VBACodeBlock 
                  modules={modules} 
                  screenshotUrl={macro?.screenshot_url}
                  excelFiles={excelFiles}
                  onCopy={handleCopyCode} 
                />
              </div>
            )}

            {/* ⭐ 2. VIDEO TITLE */}
            <h1 className="text-[28px] sm:text-[32px] font-bold tracking-tight leading-tight mb-4">{macro.name}</h1>

            {/* ⭐ 3. CREATOR BAR */}
            <div className="mb-4">
              {/* Desktop: Full profile info on left, buttons on right */}
              <div className="hidden md:flex items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <Link to={`/profile/${macro.public_profiles?.username || "unknown"}`} className="flex-shrink-0">
                    <Avatar className="h-10 w-10 hover:opacity-80 transition-opacity">
                      <AvatarImage src={macro.public_profiles?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                  </Link>
                  <div className="flex flex-col">
                    <Link to={`/profile/${macro.public_profiles?.username || "unknown"}`} className="flex items-center gap-1.5 group">
                      <span className="font-semibold text-base group-hover:text-primary transition-colors">
                        {macro.public_profiles?.full_name || macro.public_profiles?.username || "Unknown User"}
                      </span>
                      {macro.public_profiles?.verified && <img src={verifiedBadge} alt="Verified" className="h-4 w-4" />}
                    </Link>
                    <span className="text-xs text-muted-foreground">{formatFollowerCount(authorFollowerCount)} followers</span>
                  </div>
                </div>

                {/* Right Side: Engagement Buttons - Desktop */}
                <div className="flex items-center gap-2">
                  <div className="flex items-center rounded-full bg-muted overflow-hidden border border-border/30">
                    <button
                      onClick={handleLike}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 hover:bg-background/50 transition-all duration-200 border-r border-border/30",
                        likeAnimation && "scale-110",
                      )}
                    >
                      <ThumbsUp
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          isLiked && "fill-current",
                          likeAnimation && "scale-125",
                        )}
                      />
                      <span className={cn("text-sm transition-all duration-200", isLiked && "font-bold")}>
                        {likesCount > 0 ? likesCount.toLocaleString() : "Like"}
                      </span>
                    </button>
                    <button
                      onClick={handleDislike}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 hover:bg-background/50 transition-all duration-200",
                        dislikeAnimation && "scale-110",
                      )}
                    >
                      <ThumbsDown
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          isDisliked && "fill-current",
                          dislikeAnimation && "scale-125",
                        )}
                      />
                    </button>
                  </div>

                  {!isMobile && (
                    <Popover open={showAddToExcelPopover} onOpenChange={setShowAddToExcelPopover}>
                      {showAddToExcelPopover && (
                        <div
                          className="fixed inset-0 bg-background/60 backdrop-blur-[2px] z-[9998] animate-fade-in"
                          onClick={() => setShowAddToExcelPopover(false)}
                        />
                      )}
                      <PopoverTrigger asChild>
                        <Button
                          onClick={handleAddToExcel}
                          data-add-to-excel
                          variant="ghost"
                          className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted text-foreground border border-border/30 hover:bg-background/50 hover:text-foreground transition-colors text-sm font-medium h-auto"
                        >
                          {showAddToExcelPopover ? (
                            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <ExternalLink className="h-4 w-4" />
                          )}
                          Add to Excel
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent
                        side="top"
                        className="w-[480px] z-[9999] rounded-2xl shadow-2xl border-2 bg-popover p-6 animate-scale-in !fixed !left-1/2 !-translate-x-1/2"
                        onInteractOutside={() => setShowAddToExcelPopover(false)}
                        sideOffset={335}
                      >
                        <div className="space-y-5">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2">
                              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-green-500/10">
                                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400" />
                              </div>
                              <h4 className="font-semibold text-base">Macro copied to clipboard</h4>
                            </div>
                            <div className="flex items-center gap-0.5 p-0.5 rounded-lg bg-muted/50">
                              <button
                                onClick={() => setSelectedOS("windows")}
                                className={cn(
                                  "flex items-center justify-center w-8 h-8 rounded-md transition-all",
                                  selectedOS === "windows"
                                    ? "bg-foreground/90 text-background shadow-sm"
                                    : "hover:bg-background/50 opacity-50",
                                )}
                              >
                                <svg className="h-4 w-4" viewBox="0 0 88 88" fill="currentColor">
                                  <path d="M0 12.402l35.687-4.86.016 34.423-35.67.203zm35.67 33.529l.028 34.453L.028 75.48.026 45.7zm4.326-39.025L87.314 0v41.527l-47.318.376zm47.329 39.349l-.011 41.34-47.318-6.678-.066-34.739z" />
                                </svg>
                              </button>
                              <button
                                onClick={() => setSelectedOS("mac")}
                                className={cn(
                                  "flex items-center justify-center w-8 h-8 rounded-md transition-all",
                                  selectedOS === "mac"
                                    ? "bg-foreground/90 text-background shadow-sm"
                                    : "hover:bg-background/50 opacity-50",
                                )}
                              >
                                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                                </svg>
                              </button>
                            </div>
                          </div>
                          <div className="space-y-4 text-sm text-muted-foreground pl-10">
                            <div className="space-y-2">
                              <p className="font-medium text-foreground text-base">Step 1:</p>
                              <p className="text-base">
                                Open Excel and press{" "}
                                <kbd className="px-2 py-1 bg-muted rounded-md text-xs font-mono border border-border shadow-sm">
                                  {selectedOS === "windows" ? "Alt + F11" : "fn + option + F11"}
                                </kbd>{" "}
                                to open the VBA editor
                              </p>
                            </div>
                            <div className="space-y-2">
                              <p className="font-medium text-foreground text-base">Step 2:</p>
                              <p className="text-base flex items-center gap-1.5">
                                In the left panel, right-click
                                <img
                                  src={mouseIcon}
                                  alt="right-click"
                                  className="h-3.5 w-3.5 opacity-70 flex-shrink-0 dark:invert"
                                />
                                your workbook
                              </p>
                              <ul className="pl-4 space-y-1 text-base list-disc list-inside">
                                <li>
                                  Select <span className="font-medium text-foreground">Insert → Module</span>
                                </li>
                                <li>Paste the macro</li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  )}

                  <button
                    onClick={handleSaveToggle}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted border border-border/30 hover:bg-background/50 transition-colors text-sm font-medium"
                  >
                    <Bookmark className={`h-4 w-4 ${isSaved ? "fill-current" : ""}`} />
                    {isSaved ? "Saved" : "Save"}
                  </button>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted border border-border/30 hover:bg-background/50 transition-colors text-sm font-medium">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="z-[9999] bg-background">
                      <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownloadBas} className="cursor-pointer">
                        <Download className="h-4 w-4 mr-2" />
                        Download .bas
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowReportDialog(true)} className="cursor-pointer">
                        <Flag className="h-4 w-4 mr-2" />
                        Report Macro
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* Mobile: Horizontal scrollable with avatar + buttons */}
              <div className="md:hidden overflow-x-auto scrollbar-hide">
                <div className="flex items-center gap-2 min-w-max">
                  {/* Avatar */}
                  <Link to={`/profile/${macro.public_profiles?.username || "unknown"}`} className="flex-shrink-0">
                    <Avatar className="h-10 w-10 hover:opacity-80 transition-opacity">
                      <AvatarImage src={macro.public_profiles?.avatar_url || undefined} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        <User className="h-5 w-5" />
                      </AvatarFallback>
                    </Avatar>
                  </Link>

                  {/* Like/Dislike buttons */}
                  <div className="flex items-center rounded-full bg-muted overflow-hidden border border-border/30">
                    <button
                      onClick={handleLike}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 hover:bg-background/50 transition-all duration-200 border-r border-border/30",
                        likeAnimation && "scale-110",
                      )}
                    >
                      <ThumbsUp
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          isLiked && "fill-current",
                          likeAnimation && "scale-125",
                        )}
                      />
                      <span className={cn("text-sm transition-all duration-200", isLiked && "font-bold")}>
                        {likesCount > 0 ? likesCount.toLocaleString() : "Like"}
                      </span>
                    </button>
                    <button
                      onClick={handleDislike}
                      className={cn(
                        "flex items-center gap-1.5 px-4 py-2 hover:bg-background/50 transition-all duration-200",
                        dislikeAnimation && "scale-110",
                      )}
                    >
                      <ThumbsDown
                        className={cn(
                          "h-4 w-4 transition-all duration-200",
                          isDisliked && "fill-current",
                          dislikeAnimation && "scale-125",
                        )}
                      />
                    </button>
                  </div>

                  {/* Add to Excel button */}
                  {isMobile && (
                    <button
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        await handleAddToExcel();
                        setIsCopiedMobile(true);
                        setTimeout(() => setIsCopiedMobile(false), 2000);
                      }}
                      data-add-to-excel
                      className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted text-foreground border border-border/30 hover:bg-background/50 hover:text-foreground transition-colors text-sm font-medium whitespace-nowrap"
                    >
                      {isCopiedMobile ? (
                        <>
                          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                          Copied
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4" />
                          Add to Excel
                        </>
                      )}
                    </button>
                  )}

                  {/* Save button */}
                  <button
                    onClick={handleSaveToggle}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted border border-border/30 hover:bg-background/50 transition-colors text-sm font-medium"
                  >
                    <Bookmark className={`h-4 w-4 ${isSaved ? "fill-current" : ""}`} />
                    {isSaved ? "Saved" : "Save"}
                  </button>

                  {/* More options dropdown */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="flex items-center gap-1.5 px-4 py-2 rounded-full bg-muted border border-border/30 hover:bg-background/50 transition-colors text-sm font-medium">
                        <MoreHorizontal className="h-4 w-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="z-[9999] bg-background">
                      <DropdownMenuItem onClick={handleShare} className="cursor-pointer">
                        <Share2 className="h-4 w-4 mr-2" />
                        Share
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={handleDownloadBas} className="cursor-pointer">
                        <Download className="h-4 w-4 mr-2" />
                        Download .bas
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowReportDialog(true)} className="cursor-pointer">
                        <Flag className="h-4 w-4 mr-2" />
                        Report Macro
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>

            {/* ⭐ 5. DESCRIPTION BOX (YouTube style collapsible) */}
            <div className="rounded-xl bg-muted p-4 text-sm leading-relaxed mb-8 border border-border/30">
              {/* Stats row at top */}
              <div className="text-sm text-muted-foreground pb-1.5">
                {(macro.installs_count || 0).toLocaleString()} uses · 3 days ago
              </div>

              {/* Description - Initially truncated */}
              {macro.short_desc && (
                <div className="pt-1.5">
                  <p className="text-foreground/90 leading-relaxed">
                    {macro.short_desc}{' '}
                    {!showFullDescription && (
                      <button
                        onClick={() => setShowFullDescription(true)}
                        className="text-sm font-medium hover:text-foreground transition-colors inline"
                      >
                        ...more
                      </button>
                    )}
                  </p>
                </div>
              )}

              {/* Expandable section */}
              {showFullDescription && (
                <>
                  {/* Installation Guide */}
                  {macro.installation_guide && (
                    <div className="pt-3 mt-3 border-t border-border/20">
                      <h3 className="text-base font-semibold mb-2">Installation & Usage Guide</h3>
                      <div className="prose prose-sm dark:prose-invert max-w-none prose-ol:my-4 prose-ul:my-4 prose-li:my-1 prose-p:my-3">
                        <ReactMarkdown 
                          rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        >
                          {macro.installation_guide}
                        </ReactMarkdown>
                      </div>
                    </div>
                  )}
                  <button
                    onClick={() => setShowFullDescription(false)}
                    className="text-sm font-medium hover:text-foreground transition-colors inline-block mt-2"
                  >
                    Show less
                  </button>
                </>
              )}
            </div>

            {/* ⭐ 7. COMMENTS SECTION */}
            <div className="mt-10">
              <DiscussionSection
                macroId={macro.id}
                user={user}
                sortBy={discussionSortBy}
                onSortChange={setDiscussionSortBy}
                onReviewUpdate={() => fetchAverageRating(macro.id)}
              />
            </div>
          </div>
          {/* End of Left Column */}

          {/* Right Column: Recommended Macros Sidebar */}
          <div className="hidden lg:block -mt-4">
            {recommendedMacros.length > 0 && (
              <div className="flex flex-col gap-3 w-full h-auto">
                {recommendedMacros.map((recommendedMacro) => (
                  <RecommendedMacroCard key={recommendedMacro.id} macro={recommendedMacro} />
                ))}
              </div>
            )}
          </div>
          {/* End of Right Column */}
        </div>
        {/* End of Grid */}

        {/* Report Dialog */}
        <AlertDialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Report Macro</AlertDialogTitle>
              <AlertDialogDescription>
                Please describe why you're reporting this macro. Our team will review your report.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <Textarea
              placeholder="Describe the issue..."
              value={reportReason}
              onChange={(e) => setReportReason(e.target.value)}
              className="min-h-[100px]"
            />
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleReport}>Submit Report</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};
export default MacroDetail;
